import React from 'react';
import { Card, Typography } from 'antd';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

const { Title, Text } = Typography;

export interface SectionCardProps extends Styleable {
    title?: React.ReactNode;
    description?: React.ReactNode;
    extra?: React.ReactNode;
    children: React.ReactNode;
    bodyPadded?: boolean;
    loading?: boolean;
    /**
     * Class merged onto the card's BODY element, after the card's own body
     * class. This is the only sanctioned channel for a page to adjust the
     * spacing/overflow of the region its content actually sits in: without it
     * a caller has to reach through the card with a
     * `.my-page .ant-card-body > …` descendant selector, which breaks the
     * moment the card's internals move (EAA-1839).
     *
     * Layout facts about ONE instance only — padding, overflow, a corner
     * radius the body must clip to. A look the card should support everywhere
     * belongs in the component, not here.
     */
    bodyClassName?: string;
}

/**
 * Tier-1 presentational primitive: a titled content card for grouping
 * related fields or tables. Subtle ring and low-elevation shadow only —
 * never a heavy/dark border. `bodyPadded={false}` strips the body padding so
 * a flush-edge child (e.g. `DataTable`) can sit directly against the card
 * edge.
 *
 * EAA-1843: the separating edge is a `box-shadow` RING, not a `border`. A
 * border occupies layout, so a card filling a fixed-height flex region came out
 * 2px taller than the space it was given; a ring costs no layout and cannot.
 * The token falls back to the previous 1px border colour, so an app that has
 * not defined `--shadow-card-ring` renders exactly as before.
 */
export function SectionCard({
    title,
    description,
    extra,
    children,
    bodyPadded = true,
    loading = false,
    className,
    bodyClassName,
    style,
}: SectionCardProps): React.ReactElement {
    const colors = useUIColors();

    const header = title ? (
        <div>
            <Title
                level={5}
                style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}
            >
                {title}
            </Title>
            {description && (
                <Text type="secondary" style={{ color: colors.textMuted, fontWeight: 'normal' }}>
                    {description}
                </Text>
            )}
        </div>
    ) : undefined;

    return (
        <Card
            className={cx('section-card', className)}
            title={header}
            extra={extra}
            loading={loading}
            style={{
                border: 'none',
                boxShadow: `var(--shadow-card-ring, 0 0 0 1px ${colors.border}), var(--shadow-sm)`,
                ...style,
            }}
            // EAA-1839: `classNames.body` is appended AFTER antd's own
            // `ant-card-body`, so an equal-specificity caller rule wins without
            // `!important` — the merge order the kit's className contract states.
            // Omitted ⇒ `undefined` ⇒ byte-identical markup for every existing caller.
            classNames={{ body: bodyClassName }}
            styles={{
                // EAA-1846 (design frame 45/98): the sheet's card is 14px 18px
                // header / 18px body — antd's own defaults (24px throughout)
                // read visibly airier than the design everywhere this card is
                // used, not just one page.
                header: { padding: '14px 18px', minHeight: 'auto' },
                body: { padding: bodyPadded ? 18 : 0 },
            }}
        >
            {children}
        </Card>
    );
}

export default SectionCard;
